/**
 * The Manager Meeting entrance: a seven-second clip with its own sound, played once when a person
 * enters the Manager Meeting from their meetings list (contexts/20, "The Manager Meeting entrance").
 * The rules of who sees it and when, pure so they are tested; the player is
 * `components/MeetingEntrance.tsx`.
 */

import type { KeyValueStore } from './storedFlag.js';

/** The one meeting that has an entrance: the Manager Meeting. */
export const ENTRANCE_MEETING_ID = 5;

/** No first frame this long after the click: the meeting shows without it. `waiting` before then is normal. */
export const ENTRANCE_STARTUP_TIMEOUT_MS = 4_000;
/** The clip runs 7.07 s; anything still on screen this long after the click is dismissed. */
export const ENTRANCE_OVERALL_TIMEOUT_MS = 20_000;
/** The video fades over this much of the clip's clean ending, revealing the same frame as a still. */
export const ENTRANCE_FADE_LEAD_S = 0.45;
/** The still fades into the page after the video has gone; the two together, in CSS (`.entrance--leaving`). */
export const ENTRANCE_LEAVE_MS = 450 + 650;

export function entranceStorageKey(userId: string, meetingId: number): string {
  return `unitedos.meeting_entrance.${userId}.${meetingId}`;
}

/** The address of the meeting and any of its tabs — the intro keeps playing across them and stops anywhere else. */
export function isEntranceRoute(pathname: string, meetingId: number = ENTRANCE_MEETING_ID): boolean {
  const base = `/meetings/${meetingId}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/** Only a plain left click enters in this tab; a middle-click or Ctrl/⌘/Shift-click opens elsewhere. */
export function isPlainClick(e: { readonly button: number; readonly ctrlKey: boolean; readonly metaKey: boolean; readonly shiftKey: boolean; readonly altKey: boolean }): boolean {
  return e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey && !e.altKey;
}

/** What the browser tells us about itself; a test hands in a plain object. */
export interface BrowserTraits {
  readonly userAgent: string;
  /** `navigator.userAgentData.brands`, where the browser has it (Chromium). */
  readonly brands?: readonly { readonly brand: string }[] | undefined;
  readonly mobile?: boolean | undefined;
  readonly reducedMotion: boolean;
}

/**
 * Desktop Chrome with motion allowed — the one browser the clip was checked in. Safari, Firefox,
 * Edge, phones and reduced motion get the plain meeting page, and never fetch the video.
 */
export function canPlayEntrance(t: BrowserTraits): boolean {
  if (t.reducedMotion || t.mobile === true) return false;
  if (t.brands && t.brands.length > 0) return t.brands.some((b) => b.brand === 'Google Chrome');
  const ua = t.userAgent;
  return /\bChrome\/\d/.test(ua) && !/\b(Edg|OPR|SamsungBrowser|CriOS)\//.test(ua) && !/\b(Mobile|Android)\b/.test(ua);
}

/**
 * Whether this click should play the entrance: the Manager Meeting, the person attends it (the
 * server's answer in the meetings list), Chrome on a desktop, and not already tried in this tab —
 * a Skip or a failure counts as tried, so nothing replays on a remount.
 */
export function shouldPlayEntrance(input: {
  readonly meetingId: number;
  readonly attending: boolean;
  readonly userId: string;
  readonly canPlay: boolean;
  readonly store: KeyValueStore | null;
  readonly tried: ReadonlySet<string>;
}): boolean {
  if (input.meetingId !== ENTRANCE_MEETING_ID || !input.attending || input.userId === '' || !input.canPlay) return false;
  const key = entranceStorageKey(input.userId, input.meetingId);
  if (input.tried.has(key)) return false;
  try {
    return input.store?.getItem(key) !== '1';
  } catch {
    return true;
  }
}

/** Marks the attempt at click time, in the tab's storage and in memory (a private window may refuse the storage). */
export function markEntranceTried(userId: string, meetingId: number, store: KeyValueStore | null, tried: Set<string>): void {
  const key = entranceStorageKey(userId, meetingId);
  tried.add(key);
  try {
    store?.setItem(key, '1');
  } catch {
    // Refused: the in-memory mark still holds for this page load.
  }
}

/** Whether the clip has reached its clean ending, where the video fades into the still. */
export function inFadeWindow(currentTime: number, duration: number): boolean {
  return Number.isFinite(duration) && duration > 0 && duration - currentTime <= ENTRANCE_FADE_LEAD_S;
}

/** `window.sessionStorage` — once per browser tab — or null where touching it throws. */
export function tabStorage(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
